import { Book, ImageTransform, PageLayout, PageLayouts, PageLayoutSlot, CoverLayouts, getSpineWidthForDoublePageCountInMm } from '@/models/book/book'
import { AlbumFile } from '@/models/albumFile.ts'
import { PlaylistDoublePage, PlaylistDoublePageRole } from '@/models/playlist.ts'

// Custom error for when there are too many images
export class TooManyImagesError extends Error {
  maxImages: number
  requestedImages: number

  constructor(requestedImages: number, maxImages: number) {
    super(`Cannot fit ${requestedImages} images. Maximum: ${maxImages}`)
    this.name = 'TooManyImagesError'
    this.maxImages = maxImages
    this.requestedImages = requestedImages
  }
}

// Helper to calculate max possible images for a given maxPages
export function getMaxImagesForBook(maxPages: number = 50): number {
  let maxSlotsAvailable = 0
  PageLayouts.forEach(layout => {
    maxSlotsAvailable = Math.max(maxSlotsAvailable, layout.slots.length)
  })

  let maxCoverSlots = 0
  CoverLayouts.forEach(layout => {
    maxCoverSlots = Math.max(maxCoverSlots, layout.slots.length)
  })

  return maxCoverSlots + (maxPages - 1) * maxSlotsAvailable
}

function randomId() {
  let randomId: string
  try {
    randomId = crypto.randomUUID()
  } catch (e) {
    console.log('Err generating random id', e)
    randomId = `${Math.random()}:${Math.random()}:${Math.random()}`
  }

  return randomId
}

// function placeFullSizeImage() {
//
// }
//
// export function bookFromPageLayout(files: Array<AlbumFile>, pageWidth: number, pageHeight: number): { book:  Book, pages: Array<BookPage>, images: Array<ImageTransform> } {
//   const pages: BookPage[] = []
//   const images: Array<ImageTransform> = []
//   let pageNumber = 0
// }
//
// function placeImagesByLayout(page: PlaylistDoublePage, layout: PageLayout, i: number, files: Array<AlbumFile>): Array<ImageTransform> {
//   const placed = []
//   for (const slot in layout.slots) {
//
//   }
//
//   return placed
// }

export function placeImageByArea(slot: { width: number, height: number }, file: AlbumFile): {
  crop_x: number
  crop_y: number
  crop_height: number
  crop_width: number
} {
  let crop_x = 0, crop_y = 0, crop_width = 100, crop_height = 100
  if ('use_maximum_natural_size' in slot && slot.use_maximum_natural_size) {
    throw 'not implemented yet'
  } else {
    const slotRatio = slot.width / slot.height
    const imgRatio = file.width! / file.height!

    if (slotRatio > imgRatio) {
      crop_width = 100
      crop_height = (imgRatio / slotRatio) * 100
      crop_x = 0
      crop_y = (100 - crop_height) / 2
    } else {
      crop_height = 100
      crop_width = (slotRatio / imgRatio) * 100
      crop_x = (100 - crop_width) / 2
      crop_y = 0
    }
  }

  return {
    crop_x,
    crop_y,
    crop_height,
    crop_width,
  }
}

export function placeImageBySlot(page: PlaylistDoublePage, slot: PageLayoutSlot, file?: AlbumFile | null): ImageTransform {
  let width, height, x, y
  let crop_x = 0, crop_y = 0, crop_width = 100, crop_height = 100, crop_rotated = false

  if ('use_maximum_natural_size' in slot && slot.use_maximum_natural_size) {
    // const pageWidth = page.width * slot.maxW
    // const pageHeight = page.height * slot.maxH
    // const offsetX = slot.offsetX * page.width
    // const borderPercentage = slot.border
    // const ratio = Math.min(pageWidth / file.width!, pageHeight / file.height!) * (1-borderPercentage*2)
    // width = Math.floor(file.width! * ratio)
    // height = Math.floor(file.height! * ratio)
    // x = Math.floor(pageWidth / 2 - width / 2 + offsetX)
    // y = Math.floor(pageHeight / 2 - height / 2)
    throw 'not implemented'
  } else {
    // Handle cover pages with spine
    if (page.role === PlaylistDoublePageRole.COVER && page.spine_width > 0) {
      const spineWidthPx = page.spine_width

      // The page width represents the full spread (back + spine + front)
      const halfPageWidth = (page.width - spineWidthPx) / 2

      // Check if slot spans the spine
      if (slot.x < 0.5 && slot.x + slot.width > 0.5) {
        // Slot spans across the spine
        const leftPortionRatio = (0.5 - slot.x) / slot.width
        const rightPortionRatio = (slot.x + slot.width - 0.5) / slot.width

        // Calculate actual widths
        const leftWidth = leftPortionRatio * slot.width * halfPageWidth
        const rightWidth = rightPortionRatio * slot.width * halfPageWidth
        width = leftWidth + spineWidthPx + rightWidth

        // X position starts from the normalized position on the back page
        x = slot.x * halfPageWidth
        y = page.height * slot.y
        height = page.height * slot.height
      } else if (slot.x < 0.5) {
        // Slot is on the back page (left side)
        width = halfPageWidth * slot.width * 2 // scale to half page
        height = page.height * slot.height
        x = halfPageWidth * slot.x * 2 // scale to half page
        y = page.height * slot.y
      } else {
        // Slot is on the front page (right side)
        // Adjust x position to account for spine
        const adjustedX = (slot.x - 0.5) * 2 // Convert from 0.5-1 range to 0-1 range
        width = halfPageWidth * slot.width * 2 // scale to half page
        height = page.height * slot.height
        x = halfPageWidth + spineWidthPx + (adjustedX * halfPageWidth)
        y = page.height * slot.y
      }
    } else {
      // Regular page without spine
      width = page.width * slot.width
      height = page.height * slot.height
      x = page.width * slot.x
      y = page.height * slot.y
    }
    if (file) {
      const placement = placeImageByArea({width, height}, file)
      crop_x = placement.crop_x
      crop_y = placement.crop_y
      crop_width = placement.crop_width
      crop_height = placement.crop_height
      crop_rotated = false
    }
  }

  return {
    id: randomId(),
    img: file || null,
    page: page.id,
    kind: 'img',
    order: slot.order,
    rotation: 0,
    x,
    y,
    width,
    height,
    crop_x,
    crop_y,
    crop_height,
    crop_width,
    crop_rotated,
  }
}

export function bookFromAlbumFiles(files: Array<AlbumFile>, pageWidth: number, pageHeight: number): { book:  Book, pages: Array<PlaylistDoublePage>, images: Array<ImageTransform> } {
  const pages: PlaylistDoublePage[] = []
  const images: Array<ImageTransform> = []
  let pageNumber = 0

  for (let i = 0; i < files.length; i += 2) {
    const page: PlaylistDoublePage = {
      id: randomId(),
      layout_id: '',
      role: PlaylistDoublePageRole.PAGE,
      order: pageNumber++,
      width: 2*pageWidth,
      height: pageHeight,
      spine_width: 0,
    }
    pages.push(page)

    images.push(placeImageBySlot(page, PageLayouts[0].slots[0], files[i]))
    if (i + 1 < files.length) {
      images.push(placeImageBySlot(page, PageLayouts[0].slots[1], files[i+1]))
    }
  }

  return {
    book: {
      pages,
      page_width: pageWidth,
      page_height: pageHeight,
    },
    pages,
    images,
  }
}

export function bookFromAlbumFilesSmart(files: Array<AlbumFile>, pageWidth: number, pageHeight: number, minPages: number = 11, maxPages: number = 50): { book:  Book, pages: Array<PlaylistDoublePage>, images: Array<ImageTransform> } {
  const pages: PlaylistDoublePage[] = []
  const images: Array<ImageTransform> = []
  let pageNumber = 0
  let fileIndex = 0

  const fileCount = files.length

  // Group layouts by slot count
  const layoutsByCount: Record<number, PageLayout[]> = {}
  let maxSlotsAvailable = 0
  PageLayouts.forEach(layout => {
    const count = layout.slots.length
    if (!layoutsByCount[count]) {
      layoutsByCount[count] = []
    }
    layoutsByCount[count].push(layout)
    maxSlotsAvailable = Math.max(maxSlotsAvailable, count)
  })

  // Also check cover layouts for max slots
  let maxCoverSlots = 0
  CoverLayouts.forEach(layout => {
    maxCoverSlots = Math.max(maxCoverSlots, layout.slots.length)
  })

  // Check if it's possible to fit all files within maxPages
  // maxPages includes cover, so content pages = maxPages - 1
  const maxPossibleImages = maxCoverSlots + (maxPages - 1) * maxSlotsAvailable
  if (fileCount > maxPossibleImages) {
    throw new TooManyImagesError(fileCount, maxPossibleImages)
  }

  // Calculate target pages based on file count
  let targetPages: number
  if (fileCount <= 20) {
    targetPages = Math.max(minPages, Math.ceil(fileCount / 2) + 1)
  } else if (fileCount <= 40) {
    targetPages = Math.max(minPages, Math.ceil(fileCount / 2.5) + 1)
  } else if (fileCount <= 60) {
    targetPages = Math.max(minPages, Math.ceil(fileCount / 3) + 1)
  } else if (fileCount <= 100) {
    targetPages = Math.max(minPages, Math.ceil(fileCount / 3.5) + 1)
  } else if (fileCount <= 150) {
    targetPages = Math.max(minPages, Math.ceil(fileCount / 4) + 1)
  } else if (fileCount <= 250) {
    targetPages = Math.max(minPages, Math.ceil(fileCount / 5) + 1)
  } else {
    targetPages = Math.max(minPages, Math.ceil(fileCount / 6) + 1)
  }

  // Enforce maximum pages limit
  targetPages = Math.min(targetPages, maxPages)

  // Get spine width for the total page count
  const spineWidthMm = getSpineWidthForDoublePageCountInMm(targetPages)
  const spineWidthPx = spineWidthMm * 12 // 12px per mm at 300dpi

  // Create cover page first
  const coverLayout = CoverLayouts[Math.floor(Math.random() * CoverLayouts.length)]
  const coverPage: PlaylistDoublePage = {
    id: randomId(),
    layout_id: coverLayout.id,
    role: PlaylistDoublePageRole.COVER,
    order: pageNumber++,
    width: Math.floor(2 * pageWidth + spineWidthPx),
    height: pageHeight,
    spine_width: spineWidthPx,
  }
  pages.push(coverPage)

  // Place images on cover if available
  const coverSlotsToFill = Math.min(coverLayout.slots.length, fileCount)
  for (let i = 0; i < coverSlotsToFill; i++) {
    images.push(placeImageBySlot(coverPage, coverLayout.slots[i], files[fileIndex]))
    fileIndex++
  }

  // Calculate remaining files after cover
  const filesAfterCover = fileCount - fileIndex

  // Step 1: Calculate how many content pages we need (aesthetic distribution)
  let contentPagesNeeded: number
  if (fileCount <= 20) {
    contentPagesNeeded = Math.ceil(filesAfterCover / 2)
  } else if (fileCount <= 40) {
    contentPagesNeeded = Math.ceil(filesAfterCover / 2.5)
  } else if (fileCount <= 60) {
    contentPagesNeeded = Math.ceil(filesAfterCover / 3)
  } else if (fileCount <= 100) {
    contentPagesNeeded = Math.ceil(filesAfterCover / 3.5)
  } else if (fileCount <= 150) {
    contentPagesNeeded = Math.ceil(filesAfterCover / 4)
  } else if (fileCount <= 250) {
    contentPagesNeeded = Math.ceil(filesAfterCover / 5)
  } else {
    contentPagesNeeded = Math.ceil(filesAfterCover / 6)
  }

  // Ensure we have at least minPages total (including cover)
  contentPagesNeeded = Math.max(contentPagesNeeded, minPages - 1)

  // Ensure we have enough pages for all images (based on max slots per layout)
  const minContentPagesForAllImages = Math.ceil(filesAfterCover / maxSlotsAvailable)
  contentPagesNeeded = Math.max(contentPagesNeeded, minContentPagesForAllImages)

  // Cap at maxPages (including cover)
  contentPagesNeeded = Math.min(contentPagesNeeded, maxPages - 1)

  // Helper to find a layout with exactly or close to targetSlots
  function findLayoutForSlots(targetSlots: number): PageLayout {
    // Try exact match first
    if (layoutsByCount[targetSlots]?.length > 0) {
      const layouts = layoutsByCount[targetSlots]
      return layouts[Math.floor(Math.random() * layouts.length)]
    }

    // Try larger layouts (prefer slightly larger over smaller to fit all images)
    for (let count = targetSlots + 1; count <= maxSlotsAvailable; count++) {
      if (layoutsByCount[count]?.length > 0) {
        const layouts = layoutsByCount[count]
        return layouts[Math.floor(Math.random() * layouts.length)]
      }
    }

    // Try smaller layouts
    for (let count = targetSlots - 1; count >= 1; count--) {
      if (layoutsByCount[count]?.length > 0) {
        const layouts = layoutsByCount[count]
        return layouts[Math.floor(Math.random() * layouts.length)]
      }
    }

    return PageLayouts[0]
  }

  // Step 2: Create content pages with even distribution
  for (let i = 0; i < contentPagesNeeded && fileIndex < fileCount; i++) {
    const remainingFiles = fileCount - fileIndex
    const pagesLeft = contentPagesNeeded - i

    // Calculate how many images this page should have (distribute evenly)
    const imagesThisPage = Math.ceil(remainingFiles / pagesLeft)

    // Find a layout that fits this number of images
    const selectedLayout = findLayoutForSlots(Math.min(imagesThisPage, maxSlotsAvailable))

    const page: PlaylistDoublePage = {
      id: randomId(),
      layout_id: selectedLayout.id,
      role: PlaylistDoublePageRole.PAGE,
      order: pageNumber++,
      width: 2 * pageWidth,
      height: pageHeight,
      spine_width: 0,
    }
    pages.push(page)

    // Fill all slots in the layout (up to remaining files)
    const slotsToFill = Math.min(selectedLayout.slots.length, remainingFiles)
    for (let j = 0; j < slotsToFill; j++) {
      images.push(placeImageBySlot(page, selectedLayout.slots[j], files[fileIndex]))
      fileIndex++
    }
  }

  // Step 3: Safety net - handle any remaining files
  while (fileIndex < fileCount && pages.length < maxPages) {
    const remainingFiles = fileCount - fileIndex
    const pagesRemaining = maxPages - pages.length
    const slotsNeeded = Math.ceil(remainingFiles / pagesRemaining)

    const selectedLayout = findLayoutForSlots(Math.min(slotsNeeded, maxSlotsAvailable))

    const page: PlaylistDoublePage = {
      id: randomId(),
      layout_id: selectedLayout.id,
      role: PlaylistDoublePageRole.PAGE,
      order: pageNumber++,
      width: 2 * pageWidth,
      height: pageHeight,
      spine_width: 0,
    }
    pages.push(page)

    const slotsToFill = Math.min(selectedLayout.slots.length, remainingFiles)
    for (let j = 0; j < slotsToFill; j++) {
      images.push(placeImageBySlot(page, selectedLayout.slots[j], files[fileIndex]))
      fileIndex++
    }
  }

  // Step 4: Error check
  if (fileIndex < fileCount) {
    throw new Error(`Could not place all images. ${fileCount - fileIndex} images remaining after ${maxPages} pages.`)
  }

  // Step 5: Fill to minPages with empty pages (only if needed for minimum)
  while (pages.length < minPages) {
    const emptyLayout = layoutsByCount[1]?.[0] || PageLayouts[0]

    const page: PlaylistDoublePage = {
      id: randomId(),
      layout_id: emptyLayout.id,
      role: PlaylistDoublePageRole.PAGE,
      order: pageNumber++,
      width: 2 * pageWidth,
      height: pageHeight,
      spine_width: 0,
    }
    pages.push(page)
  }

  return {
    book: {
      pages,
      page_width: pageWidth,
      page_height: pageHeight,
    },
    pages,
    images,
  }
}

